import methodsJson from "../ocs-methods.json" with { type: "json" };

export { MCC_TO_ISO, mccToIso, isoToMccs } from "./mcc-iso.js";
export { LIFECYCLE_RESELLER_ID } from "./bridge4ip-ingest.js";

export type OcsScope = "read" | "write" | "admin";

export interface OcsParam {
  type: string;
  required: boolean;
}

export interface OcsMethod {
  name: string;
  ocs_method: string;
  category: string;
  scope: OcsScope;
  description: string;
  params: Record<string, OcsParam>;
  response: Record<string, unknown>;
  annotations: string;
  verified_against_server: boolean;
  verified_against_live_docs: boolean;
}

export interface OcsIntelligenceMethod {
  name: string;
  category: "intelligence";
  scope: OcsScope;
  description: string;
  wraps: string[];
  params: Record<string, OcsParam>;
  verified_against_server: boolean;
  verified_against_live_docs: boolean;
}

export interface OcsBacklogMethod {
  name: string;
  ocs_method: string;
  category: string;
  scope: OcsScope;
  source: string;
  rationale: string;
}

/**
 * v1.2 — MCP App registry entry.
 *
 * Documents server-rendered UI surfaces (resourceUri + tier gating) without
 * a corresponding OCS REST method. Listed in `ocs-methods.json#v1_app_methods`
 * so the nightly fidelity reconcile script doesn't flag the app tool names
 * (e.g. `fleet_health_app`) as drift.
 */
export interface OcsAppMethod {
  name: string;
  category: "apps";
  scope: OcsScope;
  description: string;
  min_tier: "free" | "pro" | "enterprise";
  destructive: boolean;
  destructive_step?: number;
  mfa_gated?: boolean;
  ui_resource_uri: string;
  verified_against_server: boolean;
  verified_against_live_docs: boolean;
}

export interface OcsSpec {
  version: string;
  captured_at: string;
  sources: {
    live_docs_url: string;
    server_source_repo: string;
    server_source_commit: string;
    server_source_branch: string;
    notes: string;
  };
  v1_methods: OcsMethod[];
  v1_intelligence_methods: OcsIntelligenceMethod[];
  v1_1_backlog: OcsBacklogMethod[];
  /** v1.2c — registered MCP App tools; excluded from OCS drift detection. */
  v1_app_methods: OcsAppMethod[];
}

// Cast via `unknown` because TypeScript's strict `as` check considers the
// inferred JSON shape and the declared OcsSpec insufficiently overlapping
// once optional/variant fields are present across method entries.
export const ocsSpec: OcsSpec = methodsJson as unknown as OcsSpec;
export const ocsMethods: OcsMethod[] = ocsSpec.v1_methods;
export const ocsIntelligenceMethods: OcsIntelligenceMethod[] = ocsSpec.v1_intelligence_methods;
export const ocsBacklog: OcsBacklogMethod[] = ocsSpec.v1_1_backlog;
export const ocsAppMethods: OcsAppMethod[] = ocsSpec.v1_app_methods;

export function getMethodScope(name: string): OcsScope | undefined {
  const v1 = ocsSpec.v1_methods.find((m) => m.name === name);
  if (v1) return v1.scope;
  const intel = ocsSpec.v1_intelligence_methods.find((m) => m.name === name);
  if (intel) return intel.scope;
  return undefined;
}

export function getMethodsByScope(scope: OcsScope): OcsMethod[] {
  return ocsSpec.v1_methods.filter((m) => m.scope === scope);
}

export function getAllMethodNames(): string[] {
  return [
    ...ocsSpec.v1_methods.map((m) => m.name),
    ...ocsSpec.v1_intelligence_methods.map((m) => m.name),
  ];
}
